<template>
  <!-- THE GLOBAL HOST of the one create door. It opens from the app header, so
       what it opens has to live in the app SHELL — mounted once, above every
       page. Pages keep their own ANCHORED opens (a job page's spawn, a record
       page's « Nouveau job »): those arrive here with an anchor. -->
  <FormModal :open="captureOpen" @update:open="onCaptureOpen">
    <template #header>
      <DialogTitle class="flex items-center gap-2.5">
        <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <Plus class="h-4 w-4" />
        </span>
        {{ t('records.newJob') }}
      </DialogTitle>
    </template>
    <!-- `v-if` + a key on (preset, anchor): a door is a NEW conversation every
         time it opens, never the previous one resumed (the wipe law). An
         ANCHORED open arrives with the address known and locks it — the spawn
         doors' whole point is not re-asking the customer the page already
         shows. -->
    <JobCreateForm
      v-if="captureOpen"
      :key="`${captureTemplateId ?? ''}|${captureAnchor?.serviceLocationId ?? ''}`"
      :initial-template-id="captureTemplateId"
      :initial-service-location-id="captureAnchor?.serviceLocationId"
      :origin-job-id="captureAnchor?.originJobId"
      :lock-location="!!captureAnchor"
      @success="onCaptured"
      @cancel="closeCapture"
    />
  </FormModal>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { toast } from 'vue-sonner'
import { Plus } from 'lucide-vue-next'

import FormModal from '@/components/shared/FormModal.vue'
import JobCreateForm from '@/components/shared/JobCreateForm.vue'
import { DialogTitle } from '@/components/ui/dialog'
import { useIntakeDoors } from '@/composables/useIntakeDoors'
import type { JobCreated } from '@/composables/useJobIntake'

const { t } = useI18n()
const { captureOpen, captureTemplateId, captureAnchor, closeCapture, created } = useIntakeDoors()
const router = useRouter()

const onCaptureOpen = (open: boolean) => { if (!open) closeCapture() }

// « Créer et ouvrir »: the page is the continuation of the call (crew, paper,
// notes live there now), so every card lands on it — in the state the job was
// BORN in (« Vendu » already carries its price; no door opens on arrival).
const onCaptured = (jobId: string, result: JobCreated) => {
  closeCapture()
  created({ intake: result.intake, jobId })
  toast.success(result.notice)
  void router.push(`/jobs/${jobId}`)
}
</script>
